import React, { useMemo, useEffect } from 'react';
import BloodSplatter, { BloodSplatterProps } from './BloodSplatter';
import PaintSplatter, { PaintSplatterProps } from './PaintSplatter';
import DeadInsect, { DeadInsectProps } from './DeadInsect';
import ComputerPart, { ComputerPartProps } from './ComputerPart';
import RareItem, { RareItemProps } from './RareItem';
import { touchDecalImage } from '../../utils/decalCache';

export type ThingType = 'blood' | 'paint' | 'insect' | 'tech' | 'rare';

/** The 4 regular categories that each get equal 20% weight (80% total). */
export const THING_TYPES: ThingType[] = ['blood', 'paint', 'insect', 'tech'];

export interface RandomThingProps {
  /** Optional: manually specify the type. If omitted, RandomThing randomly picks using 80/20 rarity weights. */
  type?: ThingType;
  /** Horizontal position percentage on the wall (0 - 100) */
  x?: number;
  /** Vertical position percentage along the wall (0 - 100) */
  y?: number;
  /** Rotation in degrees (0 - 360) */
  rotation?: number;
  /** Visual scale */
  scale?: number;
  /** Opacity (0 - 1) */
  opacity?: number;
  /** Optional custom image override */
  imageSrc?: string;
  /** Optional custom image array override */
  images?: string[];
  /** Optional custom +/- rotation limit (0 - 360) */
  maxRotate?: number;
  /** Optional drop shadow override */
  dropShadow?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Generic RandomThing component.
 * Can be addressed directly, or randomly picks a category using weighted rarity:
 * - 80%: one of the 4 regular categories (blood, paint, insect, tech) — each 20%
 * - 20%: rare (ciggies, fry, rhino, vape, etc.)
 */
export const RandomThing: React.FC<RandomThingProps> = ({
  type,
  x,
  y,
  rotation,
  scale,
  opacity,
  imageSrc,
  images,
  maxRotate,
  dropShadow,
  className = '',
  style
}) => {
  // Weighted category selection: 80% normal (4 equal buckets) / 20% rare
  const chosenType = useMemo<ThingType>(() => {
    if (type) return type;
    const roll = Math.random();
    if (roll < 0.20) return 'rare';
    // Remaining 80% split evenly across 4 normal categories (each 20%)
    const normalIdx = Math.floor((roll - 0.20) / 0.80 * THING_TYPES.length);
    return THING_TYPES[normalIdx];
  }, [type]);

  // Touch and renew 180-day cache for any image rendered directly by RandomThing
  useEffect(() => {
    if (imageSrc) {
      touchDecalImage(imageSrc);
    }
  }, [imageSrc]);

  const commonProps = {
    x,
    y,
    rotation,
    scale,
    opacity,
    imageSrc,
    images,
    maxRotate,
    dropShadow,
    className,
    style
  };

  switch (chosenType) {
    case 'blood':
      return <BloodSplatter {...commonProps} />;
    case 'paint':
      return <PaintSplatter {...commonProps} />;
    case 'insect':
      return <DeadInsect {...commonProps} opacity={1} />;
    case 'tech':
      return <ComputerPart {...commonProps} opacity={1} />;
    case 'rare':
      return <RareItem {...commonProps} opacity={1} />;
    default:
      return <BloodSplatter {...commonProps} />;
  }
};

export default RandomThing;
